import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleAccountBilling: React.FC = () => {
  const BASE = '/images/help/account-billing';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/ab-step-1.png`, alt: 'Subscription plans overview', caption: 'Step 1 – Coderom subscription plans and pricing tiers' },
    ],
    step2: [
      { src: `${BASE}/ab-step-2.png`, alt: 'Manage subscription settings', caption: 'Step 2 – Manage your subscription from the Settings page' },
    ],
    step3: [
      { src: `${BASE}/ab-step-3.png`, alt: 'Payment methods configuration', caption: 'Step 3 – Add or update PayPal and card payment methods' },
    ],
    step4: [
      { src: `${BASE}/ab-step-4.png`, alt: 'Invoices and billing history', caption: 'Step 4 – View and download past invoices for your records' },
    ],
    step5: [
      { src: `${BASE}/ab-step-5.png`, alt: 'Account settings page', caption: 'Step 5 – Update account email, password, timezone, and language' },
    ],
    step6: [
      { src: `${BASE}/ab-step-6.png`, alt: 'Cancel subscription confirmation', caption: 'Step 6 – Cancel your subscription' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Account, Subscription & Billing | CodeRom Help"
        description="Manage your CodeRom subscription, payment methods & account settings."
      />
      <HelpBreadcrumbSchema slug="account-billing" name="Account, subscription & billing" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Account, subscription & billing</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Account, subscription & billing</h1>
                <p className="article-subtitle">
                  Manage your Coderom subscription, update payment methods, view invoices, and control your account settings.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 6 min read</span>
                  <span className="category">Account</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. Subscription plans</a></li>
                  <li><a href="#step2">2. Manage your subscription</a></li>
                  <li><a href="#step3">3. Payment methods</a></li>
                  <li><a href="#step4">4. Invoices and billing history</a></li>
                  <li><a href="#step5">5. Account settings</a></li>
                  <li><a href="#step6">6. Cancel your subscription</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: Subscription plans</h2>
                  <p>
                    Coderom offers flexible plans based on the number of active eBay listings:
                  </p>

                  <div className="plans-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Plan</th>
                          <th>Active listings</th>
                          <th>Price</th>
                          <th>Best for</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Starter</strong></td>
                          <td>Up to 200</td>
                          <td>$14.90/month</td>
                          <td>New sellers</td>
                        </tr>
                        <tr>
                          <td><strong>Advanced</strong></td>
                          <td>Up to 500</td>
                          <td>$29.90/month</td>
                          <td>Growing businesses</td>
                        </tr>
                        <tr>
                          <td><strong>Professional</strong></td>
                          <td>Up to 1,000</td>
                          <td>$39.90/month</td>
                          <td>Growing e-commerce sellers (most popular)</td>
                        </tr>
                        <tr>
                          <td><strong>Business</strong></td>
                          <td>Up to 2,000</td>
                          <td>$69.90/month</td>
                          <td>Established sellers scaling up</td>
                        </tr>
                        <tr>
                          <td><strong>Enterprise</strong></td>
                          <td>Up to 5,000</td>
                          <td>$99.90/month</td>
                          <td>Large operations</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p>
                    All plans include core features like Automatic Ordering, Safe Repricing protection, Auto Buyer Messages, Gmail tracking integration, and 8 eBay + 8 Amazon marketplaces. Higher tiers add AI Title/Description generation, AI Smart Repricing, faster price &amp; stock sync intervals (down to 15 minutes), more eBay accounts, and access to additional HTML templates. Larger operations can choose Enterprise Plus, Agency, or Ultimate / Titan plans (up to 100,000 SKUs) — see the <a href="/pages-pricing">pricing page</a> for the full comparison. Annual billing is available with up to 25% off.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: Subscription plans" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Start with the Starter plan and upgrade as your business grows. You can change your plan at any time — upgrades take effect immediately, and you'll be charged the difference.
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Manage your subscription</h2>
                  <ol>
                    <li>Go to <strong>Settings → Subscription</strong></li>
                    <li>You'll see your current plan, billing cycle, and next payment date</li>
                    <li>Click <strong>Change Plan</strong> to upgrade or downgrade</li>
                    <li>Changes take effect immediately for upgrades</li>
                    <li>Downgrades take effect at the end of your current billing cycle</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Manage your subscription" />
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Payment methods</h2>
                  <p>
                    Coderom accepts payment via:
                  </p>
                  <ul>
                    <li><strong>PayPal</strong> — automatic recurring payments</li>
                    <li><strong>Stripe</strong> — credit/debit card payments</li>
                  </ul>
                  <p>
                    To update your payment method:
                  </p>
                  <ol>
                    <li>Go to <strong>Settings → Billing</strong></li>
                    <li>Click <strong>Update Payment Method</strong></li>
                    <li>Choose PayPal or card payment</li>
                    <li>Follow the prompts to complete setup</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Payment methods" />
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Invoices and billing history</h2>
                  <p>
                    View all your past payments:
                  </p>
                  <ol>
                    <li>Go to <strong>Settings → Billing</strong></li>
                    <li>Scroll down to <strong>Billing History</strong></li>
                    <li>Each invoice shows the date, amount, plan, and payment method</li>
                    <li>Click <strong>Download</strong> to get a PDF invoice for your records</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: Invoices and billing history" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Coderom invoices include all the details you need for business expense records and tax purposes.
                    </div>
                  </div>
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Account settings</h2>
                  <p>
                    Go to <strong>Settings → Account</strong> to manage:
                  </p>

                  <div className="account-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Email address</strong></td>
                          <td>Your login and notification email</td>
                        </tr>
                        <tr>
                          <td><strong>Password</strong></td>
                          <td>Change your account password</td>
                        </tr>
                        <tr>
                          <td><strong>Display name</strong></td>
                          <td>Your name shown in the dashboard</td>
                        </tr>
                        <tr>
                          <td><strong>Timezone</strong></td>
                          <td>Used for reports and scheduling</td>
                        </tr>
                        <tr>
                          <td><strong>Language</strong></td>
                          <td>Dashboard language preference</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step5} stepLabel="Step 5: Account settings" />
                </div>

                {/* Step 6 */}
                <div id="step6" className="content-section">
                  <h2>Step 6: Cancel your subscription</h2>
                  <p>
                    If you want to cancel your subscription:
                  </p>
                  <h3>How to cancel:</h3>
                  <ol>
                    <li>Go to <strong>Settings → Subscription</strong></li>
                    <li>Click <strong>Cancel Subscription</strong></li>
                    <li>Your account remains active until the end of the billing period</li>
                    <li>After cancellation, your eBay listings are not automatically removed</li>
                    <li>You can reactivate your account at any time</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step6} stepLabel="Step 6: Cancel your subscription" />
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that your account is set up, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/getting-started" className="next-step-card">
                      <div className="next-step-icon">🚀</div>
                      <div className="next-step-content">
                        <h4>Getting started with Coderom</h4>
                        <p>Complete setup guide from account creation to first sale</p>
                      </div>
                    </Link>
                    <Link to="/help/migration" className="next-step-card">
                      <div className="next-step-icon">🔄</div>
                      <div className="next-step-content">
                        <h4>Switch to Coderom from another platform</h4>
                        <p>Migration guide for moving your existing listings</p>
                      </div>
                    </Link>
                    <Link to="/help/pricing" className="next-step-card">
                      <div className="next-step-icon">💰</div>
                      <div className="next-step-content">
                        <h4>Smart repricing & pricing rules</h4>
                        <p>Automatically adjust prices based on Amazon changes</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .plans-table,
        .account-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .plans-table table,
        .account-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .plans-table th,
        .account-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .plans-table td,
        .account-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .plans-table tbody tr:last-child td,
        .account-table tbody tr:last-child td {
          border-bottom: none;
        }
        .plans-table tbody tr:hover,
        .account-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .plans-table,
          .account-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleAccountBilling;
